<script setup>
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import QuotesAPI from 'dashboard/api/quotes';
import NextButton from 'dashboard/components-next/button/Button.vue';

defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'opened']);

const initialAmount = ref('');
const notes = ref('');
const isSubmitting = ref(false);

const now = computed(() => {
  const d = new Date();
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
});

const openRegister = async () => {
  isSubmitting.value = true;
  try {
    const response = await QuotesAPI.createCashRegister({
      initial_amount: parseFloat(
        String(initialAmount.value || '0').replace(',', '.')
      ),
      notes: notes.value,
    });
    useAlert('Caixa aberto com sucesso!');
    emit('opened', response.data);
    initialAmount.value = '';
    notes.value = '';
  } catch (error) {
    const message =
      error?.response?.data?.error || 'Erro ao abrir o caixa.';
    useAlert(message);
  } finally {
    isSubmitting.value = false;
  }
};

const cancel = () => {
  initialAmount.value = '';
  notes.value = '';
  emit('close');
};
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    @click.self="cancel"
  >
    <div
      class="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden"
    >
      <!-- Header -->
      <div
        class="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center gap-3"
      >
        <span
          class="i-lucide-landmark text-xl text-emerald-600 dark:text-emerald-400"
        />
        <h2
          class="text-lg font-semibold text-slate-800 dark:text-slate-100"
        >
          Abrir Caixa
        </h2>
      </div>

      <!-- Body -->
      <div class="px-6 py-5 flex flex-col gap-4">
        <p class="text-sm text-slate-600 dark:text-slate-400">
          Nenhum caixa aberto encontrado. Para criar orçamentos, é
          necessário abrir um caixa primeiro.
        </p>

        <!-- Date/time (read-only) -->
        <div class="flex flex-col gap-1">
          <label
            class="text-xs font-medium text-slate-500 dark:text-slate-400"
          >
            Data/Hora de Abertura
          </label>
          <div
            class="px-3 py-2 bg-slate-100 dark:bg-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-200"
          >
            {{ now }}
          </div>
        </div>

        <!-- Initial amount -->
        <div class="flex flex-col gap-1">
          <label
            class="text-xs font-medium text-slate-500 dark:text-slate-400"
          >
            Troco / Fundo de Caixa (R$)
          </label>
          <input
            v-model="initialAmount"
            type="number"
            step="0.01"
            min="0"
            placeholder="0,00"
            class="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 text-sm"
          />
          <span class="text-xs text-slate-400 dark:text-slate-500">
            Informe o valor em dinheiro disponível no caixa (opcional)
          </span>
        </div>

        <!-- Notes -->
        <div class="flex flex-col gap-1">
          <label
            class="text-xs font-medium text-slate-500 dark:text-slate-400"
          >
            Observações (opcional)
          </label>
          <textarea
            v-model="notes"
            rows="2"
            placeholder="Anotações sobre a abertura..."
            class="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 text-sm resize-none"
          />
        </div>
      </div>

      <!-- Footer -->
      <div
        class="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2"
      >
        <NextButton
          faded
          slate
          label="Cancelar"
          @click="cancel"
        />
        <NextButton
          emerald
          label="Abrir Caixa"
          icon="i-lucide-lock-open"
          :disabled="isSubmitting"
          @click="openRegister"
        />
      </div>
    </div>
  </div>
</template>
